import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import type {
	ApiGetDataStatus,
	ShipmentStateType,
	ShipmentSearchMode,
} from '@redux/types'
import {
	ShipmentType,
	ShipmentLabelFileType,
	ShipmentFedexCreationType,
	DispatchType,
	DispatchDetailType,
	DispatchCreationType,
	DispatchUpdateType,
	SeasonType,
} from '@schemas'
import dayjs from 'dayjs'
import { readBoFilter } from '@utils/BackofficeFilter'

const today = dayjs().format('YYYY-MM-DD')

/**
 * 지난번에 고른 기준 · 시즌 · 상태 · 종류 · 채널 (`utils/BackofficeFilter`). 오더 관리와 **다른 키**에 적힌다.
 * 이 슬라이스도 persist 대상이 아니라 새로고침마다 initialState 로 돌아온다.
 */
const savedFilter = readBoFilter('shipment')

const initialState: ShipmentStateType = {
	shipmentList: [],
	shipmentListStatus: 'init',
	// 기준 · 시즌 · 상태 · 종류 · 채널은 기억하고, 월 · 기간 · 검색어는 매번 기본값이다
	searchMode: (savedFilter?.mode as ShipmentSearchMode) ?? 'month',
	searchMonth: dayjs(today).format('YYYY-MM'),
	searchSeasonId: savedFilter?.seasonId ?? '',
	seasonOptions: [],
	searchStartDate: dayjs(today).format('YYYY-MM-01'),
	searchEndDate: dayjs(today).endOf('month').format('YYYY-MM-DD'),
	// 기본은 진행중 — 완료 · 환불 · 취소는 빠진다(2026-09-29 사용자 지시). 끝난 건은 상태를 골라서 본다
	searchStatus: (savedFilter?.status as ShipmentStateType['searchStatus']) ?? 'active',
	searchKind: savedFilter?.kind ?? 'all',
	searchChannel: (savedFilter?.channel as ShipmentStateType['searchChannel']) ?? 'all',
	searchKeyword: '',
	selectedShipmentIds: [],
	scanModalShipmentId: '',

	detailDispatchId: '',
	detail: null,
	detailStatus: 'init',

	labelShipmentId: '',
	labelFile: null,
	labelFileStatus: 'init',

	orderShipments: [],
	orderShipmentsStatus: 'init',

	labelFormOpen: false,
	creating: false,
	tracking: false,
	saving: false,
	message: '',

	triggerInitShipping: false,
	triggerSearch: false,
	triggerTrack: { shipmentIds: [], orderIds: [], dispatchIds: [], refreshOrders: false },
	triggerCreateFedex: null,
	triggerPrintLabel: { shipmentId: '', orderId: '' },
	triggerCreateDispatch: null,
	triggerUpdateDispatch: null,
	triggerDeleteDispatch: '',
	triggerAddBox: '',
}

const slice = createSlice({
	name: 'shipment',
	initialState,
	reducers: {
		// trigger
		TRIGGER_INIT_SHIPPING_PAGE: (
			state: ShipmentStateType,
			action: PayloadAction<boolean>,
		) => {
			state.triggerInitShipping = action.payload
		},
		TRIGGER_SHIPMENT_SEARCH: (
			state: ShipmentStateType,
			action: PayloadAction<boolean>,
		) => {
			state.triggerSearch = action.payload
		},
		/** 추적 갱신. 비면 배송중 전체. refreshOrders 면 끝나고 오더 목록도 다시 읽는다 */
		TRIGGER_TRACK: (
			state: ShipmentStateType,
			action: PayloadAction<ShipmentStateType['triggerTrack']>,
		) => {
			state.triggerTrack = action.payload
		},
		TRIGGER_CREATE_FEDEX: (
			state: ShipmentStateType,
			action: PayloadAction<ShipmentFedexCreationType>,
		) => {
			state.triggerCreateFedex = action.payload
		},
		/** shipmentId 가 있으면 그것, 없으면 orderId 의 활성 라벨 */
		TRIGGER_PRINT_LABEL: (
			state: ShipmentStateType,
			action: PayloadAction<ShipmentStateType['triggerPrintLabel']>,
		) => {
			state.triggerPrintLabel = action.payload
		},
		/** 수동 배송 건 추가 — 사가가 만들고 자세히로 옮긴다 */
		TRIGGER_CREATE_DISPATCH: (
			state: ShipmentStateType,
			action: PayloadAction<DispatchCreationType>,
		) => {
			state.triggerCreateDispatch = action.payload
			state.saving = true
		},
		/** 배송 건 저장 — 주는 칸만 바뀐다 */
		TRIGGER_UPDATE_DISPATCH: (
			state: ShipmentStateType,
			action: PayloadAction<{ dispatchId: string; body: DispatchUpdateType }>,
		) => {
			state.triggerUpdateDispatch = action.payload
			state.saving = true
		},
		TRIGGER_DELETE_DISPATCH: (
			state: ShipmentStateType,
			action: PayloadAction<string>,
		) => {
			state.triggerDeleteDispatch = action.payload
			state.saving = true
		},
		/** 박스 추가 — 같은 주문 아래 배송 건 하나 더 */
		TRIGGER_ADD_BOX: (state: ShipmentStateType, action: PayloadAction<string>) => {
			state.triggerAddBox = action.payload
			state.saving = true
		},

		// list
		SHIPMENT_LIST: (state: ShipmentStateType, action: PayloadAction<DispatchType[]>) => {
			state.shipmentList = action.payload
		},
		SHIPMENT_LIST_STATUS: (
			state: ShipmentStateType,
			action: PayloadAction<ApiGetDataStatus>,
		) => {
			state.shipmentListStatus = action.payload
		},

		/**
		 * 자세히가 연 배송 건. '' 이면 닫힘.
		 * 정본은 URL(`/main/shipping/:dispatchId`) 이고 `Shipping.page` 가 옮겨 적는다.
		 */
		DETAIL_DISPATCH_ID: (state: ShipmentStateType, action: PayloadAction<string>) => {
			if (state.detailDispatchId !== action.payload) {
				// 다른 건으로 옮겨가면 앞 건의 사본은 버린다
				state.detail = null
				state.detailStatus = 'init'
			}
			state.detailDispatchId = action.payload
		},
		/** 자세히 한 장을 읽는다 — 사가가 받는다 */
		FETCH_DETAIL: (state: ShipmentStateType, action: PayloadAction<string>) => {
			state.detailStatus = 'loading'
		},
		DETAIL: (
			state: ShipmentStateType,
			action: PayloadAction<DispatchDetailType | null>,
		) => {
			state.detail = action.payload
		},
		DETAIL_STATUS: (
			state: ShipmentStateType,
			action: PayloadAction<ApiGetDataStatus>,
		) => {
			state.detailStatus = action.payload
		},

		/** 라벨 보기가 연 라벨. '' 이면 활성 건 */
		LABEL_SHIPMENT_ID: (state: ShipmentStateType, action: PayloadAction<string>) => {
			if (state.labelShipmentId !== action.payload) {
				state.labelFile = null
				state.labelFileStatus = 'init'
			}
			state.labelShipmentId = action.payload
		},
		/** 저장된 라벨을 읽는다(S3 경유) — 사가가 받는다 */
		FETCH_LABEL_FILE: (state: ShipmentStateType, action: PayloadAction<string>) => {
			state.labelFileStatus = 'loading'
		},
		LABEL_FILE: (
			state: ShipmentStateType,
			action: PayloadAction<ShipmentLabelFileType | null>,
		) => {
			state.labelFile = action.payload
		},
		LABEL_FILE_STATUS: (
			state: ShipmentStateType,
			action: PayloadAction<ApiGetDataStatus>,
		) => {
			state.labelFileStatus = action.payload
		},

		// order detail modal — 주문 하나의 라벨. '' 이면 비운다
		TRIGGER_ORDER_SHIPMENTS: (
			state: ShipmentStateType,
			action: PayloadAction<string>,
		) => {
			if (action.payload === '') {
				state.orderShipments = []
				state.orderShipmentsStatus = 'init'
			}
		},
		ORDER_SHIPMENTS: (
			state: ShipmentStateType,
			action: PayloadAction<ShipmentType[]>,
		) => {
			state.orderShipments = action.payload
		},
		ORDER_SHIPMENTS_STATUS: (
			state: ShipmentStateType,
			action: PayloadAction<ApiGetDataStatus>,
		) => {
			state.orderShipmentsStatus = action.payload
		},
		SEARCH_MODE: (
			state: ShipmentStateType,
			action: PayloadAction<ShipmentSearchMode>,
		) => {
			state.searchMode = action.payload
		},
		SEARCH_MONTH: (state: ShipmentStateType, action: PayloadAction<string>) => {
			state.searchMonth = action.payload
		},
		SEARCH_SEASON_ID: (state: ShipmentStateType, action: PayloadAction<string>) => {
			state.searchSeasonId = action.payload
		},
		SEASON_OPTIONS: (state: ShipmentStateType, action: PayloadAction<SeasonType[]>) => {
			state.seasonOptions = action.payload
		},
		SEARCH_START_DATE: (state: ShipmentStateType, action: PayloadAction<string>) => {
			state.searchStartDate = action.payload
		},
		SEARCH_END_DATE: (state: ShipmentStateType, action: PayloadAction<string>) => {
			state.searchEndDate = action.payload
		},
		SEARCH_STATUS: (
			state: ShipmentStateType,
			action: PayloadAction<ShipmentStateType['searchStatus']>,
		) => {
			state.searchStatus = action.payload
		},
		SEARCH_KIND: (state: ShipmentStateType, action: PayloadAction<string>) => {
			state.searchKind = action.payload
		},
		SEARCH_CHANNEL: (
			state: ShipmentStateType,
			action: PayloadAction<ShipmentStateType['searchChannel']>,
		) => {
			state.searchChannel = action.payload
		},
		SEARCH_KEYWORD: (state: ShipmentStateType, action: PayloadAction<string>) => {
			state.searchKeyword = action.payload
		},
		SELECTED_SHIPMENT_IDS: (
			state: ShipmentStateType,
			action: PayloadAction<string[]>,
		) => {
			state.selectedShipmentIds = action.payload
		},
		SCAN_MODAL_SHIPMENT_ID: (state: ShipmentStateType, action: PayloadAction<string>) => {
			state.scanModalShipmentId = action.payload
		},

		// 라벨 만들기 화면이 서 있다/내려갔다
		LABEL_FORM_OPEN: (state: ShipmentStateType, action: PayloadAction<boolean>) => {
			state.labelFormOpen = action.payload
			if (!action.payload) {
				state.creating = false
			}
			state.message = ''
		},
		CREATING: (state: ShipmentStateType, action: PayloadAction<boolean>) => {
			state.creating = action.payload
		},
		TRACKING: (state: ShipmentStateType, action: PayloadAction<boolean>) => {
			state.tracking = action.payload
		},
		SAVING: (state: ShipmentStateType, action: PayloadAction<boolean>) => {
			state.saving = action.payload
		},
		MESSAGE: (state: ShipmentStateType, action: PayloadAction<string>) => {
			state.message = action.payload
		},
	},
})

export const shipmentActions = slice.actions
export default slice.reducer
